import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { CheckCircleRounded, CloseRounded, ErrorRounded } from '@mui/icons-material';
import { ButtonBase } from '@mui/material';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { useRef } from 'react';
import { toast } from 'sonner';
import css from './Toast.module.scss';

/** A button on the toast, beside its text. Pressing it runs `onClick` and dismisses the toast. */
export type ToastAction = { label: string; onClick: () => void };

const SWIPE_THRESHOLD = 60;

export function Toast({
  id,
  variant,
  header,
  description,
  templateId,
  shakeKey,
  action,
}: {
  id?: string | number;
  variant?: 'success' | 'error';
  header?: string | React.ReactNode;
  description?: string | React.ReactNode;
  templateId?: string;
  shakeKey?: number;
  action?: ToastAction;
}) {
  const isMobile = useIsMobile();
  const touchStartX = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const dismiss = () => {
    trackingStore.toastDismissed({ toastType: templateId });
    toast.dismiss(id);
  };

  const dismissWithSwish = () => {
    if (rootRef.current) {
      rootRef.current.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      rootRef.current.style.transform = 'translateX(300px)';
      rootRef.current.style.opacity = '0';
      setTimeout(dismiss, 200);
    } else {
      dismiss();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || !rootRef.current) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    rootRef.current.style.transform = `translateX(${deltaX}px)`;
    rootRef.current.style.opacity = `${Math.max(0, 1 - Math.abs(deltaX) / 200)}`;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || !rootRef.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      const direction = deltaX > 0 ? 1 : -1;
      rootRef.current.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      rootRef.current.style.transform = `translateX(${direction * 300}px)`;
      rootRef.current.style.opacity = '0';
      setTimeout(dismiss, 200);
    } else {
      rootRef.current.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      rootRef.current.style.transform = '';
      rootRef.current.style.opacity = '';
      setTimeout(() => {
        if (rootRef.current) {
          rootRef.current.style.transition = '';
        }
      }, 200);
    }
    touchStartX.current = null;
  };

  return (
    <div
      ref={rootRef}
      className={classNames(css.root, action && css.withAction)}
      data-shake-key={shakeKey}
      key={shakeKey}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <div className={css.icon}>
        {variant === 'success' ? <CheckCircleRounded /> : <ErrorRounded />}
      </div>
      <div className={css.text}>
        <div className={css.title}>{header}</div>
        {description && <div className={css.description}>{description}</div>}
      </div>
      {action && (
        <ButtonBase
          className={css.actionButton}
          onClick={() => {
            toast.dismiss(id);
            action.onClick();
          }}
        >
          {action.label}
        </ButtonBase>
      )}
      <div className={css.cancelButton}>
        <CloseRounded onClick={isMobile ? dismissWithSwish : dismiss} />
      </div>
    </div>
  );
}
